import { useEffect, useState } from 'react'
import { api } from '../../../lib/api.js'
import { mergeConfig } from './studioConfig.js'

/**
 * `GET /api/artwork/config`, asked ONCE for the whole studio.
 *
 * The config used to be fetched inside `Studio.jsx`, which was fine while the
 * Builder was the only thing that needed it. It is not any more: the hand-off
 * stage needs the status ladder, and the product × style matrix needs the
 * `styles` list to say which cells have a beast. Three components each doing
 * their own fetch is three round trips that can disagree with each other —
 * the same defect `canvaSource.js#sharedCanvaStatus` fixed for the Canva probe.
 *
 * `state` is deliberately three-valued. `fallback` is NOT an error: the studio
 * runs on local defaults and says so on screen, because presenting a stale menu
 * as the engine's own answer is the failure worth preventing.
 */
export default function useStudioConfig() {
  const [config, setConfig] = useState(() => mergeConfig(null))
  const [state, setState] = useState('loading') // loading | ready | fallback

  useEffect(() => {
    let alive = true
    const ctrl = new AbortController()
    ;(async () => {
      try {
        const res = await api.artworkStudioConfig({ signal: ctrl.signal })
        if (!alive) return
        setConfig(mergeConfig(res))
        setState('ready')
      } catch (err) {
        if (!alive || err?.name === 'AbortError') return
        setConfig(mergeConfig(null))
        setState('fallback')
      }
    })()
    return () => { alive = false; ctrl.abort() }
  }, [])

  return { config, configState: state }
}
